import { AppComponent, Browser, BrowserState } from "@reporting/rp-app-host";
import { html, css } from "lit";
import { customElement, query } from "lit/decorators.js";
import { AnalyticsApplication } from "../..";
import { ContextBrowserAdapter } from "../../adapters/contextBrowserAdapter";
import rp from "@reporting/rp-app-host";
import { msg } from "@omegagrid/localize";
import { dom, MenuEvent } from "@omegagrid/core";
import { create } from "../../actions";

export type ContextListState = {
	browser: BrowserState;
}

@customElement('rp-mf-contexts')
export class ContextList extends AppComponent<AnalyticsApplication, ContextListState> {

	static styles = [css`
		* {
			box-sizing: border-box;
		}

		:host {
			display: flex;
			flex-direction: column;
			width: 100%;
			height: 100%;
			position: relative;
			overflow: hidden;
		}

		.controls {
			display: flex;
			flex-direction: row;
			align-items: center;
			justify-content: flex-end;
			border-bottom: 1px solid var(--og-bucket-border-color);
			flex: 0 0 25px;
		}

		.controls > * {
			height: 100%;
		}

		.controls > og-button {
			width: 25px;
		}

		.empty-warning {
			padding: 5px;
			flex: 0;
		}

		rp-browser {
			height: 100%;
			flex: 1;
		}
	`];

	@query('rp-browser')
	browser: Browser;

	@query('.empty-warning')
	emptyWarning: HTMLElement;

	get app() { return rp.applications.get('analytics') as AnalyticsApplication }
	get service() { return rp.ms('analytics') }

	layout() {
		this.browser?.layout();
	}

	_onBrowserLoad = () => {
		if (this.browser?.tree?.model?.nodes?.length) {
			dom.hideElement(this.emptyWarning);
		} else {
			dom.showElement(this.emptyWarning);
		}
	}

	_onFilterSectionClick = async () => {
		const sidebar = await rp.host.getComponent('analytics') as any;
		sidebar?.updateState({activeTab: 'filters'}, true);
	}

	_onAddMenuSelect = async (e: MenuEvent) => {
		switch (e.item.k) {
			case 'newDashboard':
				if (await create('D')) this.browser.refresh();
				break;
			case 'newSpreadsheet':
				if (await create('S')) this.browser.refresh();
				break;
		}
	}

	render = () => html`
		<div class="controls">
			<og-button
				icon="search"
				color="transparent"
				text="${msg('Search')}"
				style="flex: 1"
				@click="${() => rp.host.openCommands('report')}">
			</og-button>

			<og-button
				icon="arrows-rotate"
				color="transparent"
				@click="${() => this.browser.refresh()}">
			</og-button>

			<og-button
				icon="square-plus"
				color="transparent"
				@click="${() => this.browser.tree.toggleTreeCollapse()}">
			</og-button>

			<og-dropdown-menu
				nocaret
				dynamicwidth
				color="transparent"
				.items="${[
					{k: 'newDashboard', v: msg('New dashboard')},
					{k: 'newSpreadsheet', v: msg('New spreadsheet')}
				]}"
				@menu.select="${this._onAddMenuSelect}">
				<div slot="label"><og-icon icon="plus"></og-icon></div>
			</og-dropdown-menu>
		</div>

		<div class="empty-warning" style="display: none">
			<og-alert type="info" nocopy>
				<div slot="content">
					${msg('No saved reports available, create some in')}
					<a href="#" style="color: yellow" @click="${this._onFilterSectionClick}">${msg('Filter section')}</a>
				</div>
			</og-alert>
		</div>

		<rp-browser
			.state="${this.state?.browser || {}}"
			@browser.state="${() => {
				this.updateState({browser: this.browser.getState()});
			}}"
			@browser.load="${this._onBrowserLoad}"
			.adapter="${new ContextBrowserAdapter(this.app.modules)}">
		</rp-browser>
	`;

}
